import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  where,
  writeBatch,
  WriteBatch,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import cloneDeep from "./cloneDeep";

const BATCH_LIMIT = 450;

export type NamedRecord = {
  id: string;
  name: string;
};

export type MenuCopyPreview = {
  menu: NamedRecord;
  categories: Array<NamedRecord & { itemCount: number }>;
  items: Array<NamedRecord & { image?: string; categoryName: string }>;
  tags: NamedRecord[];
  options: NamedRecord[];
  topMenus: NamedRecord[];
  combos: NamedRecord[];
  categoryLimits: NamedRecord[];
};

export type DestinationMenuPreview = {
  id: string;
  name: string;
  categoryCount: number;
};

export type CopyCounts = {
  menu: number;
  categories: number;
  items: number;
  pictures: number;
  tags: number;
  options: number;
  topMenus: number;
  combos: number;
  categoryLimits: number;
};

export type CopyProgress = {
  label: string;
  completed: number;
  total: number;
};

export class MenuCopyError extends Error {
  counts: CopyCounts;

  constructor(message: string, counts: CopyCounts) {
    super(message);
    this.name = "MenuCopyError";
    this.counts = counts;
  }
}

type SourceDoc = {
  id: string;
  data: Record<string, any>;
};

type MenuBundle = {
  menu: SourceDoc;
  categories: SourceDoc[];
  items: SourceDoc[];
  tags: SourceDoc[];
  options: SourceDoc[];
  topMenus: SourceDoc[];
  combos: SourceDoc[];
  limits: SourceDoc[];
};

const emptyCounts = (): CopyCounts => ({
  menu: 0,
  categories: 0,
  items: 0,
  pictures: 0,
  tags: 0,
  options: 0,
  topMenus: 0,
  combos: 0,
  categoryLimits: 0,
});

const nameOf = (data: Record<string, any>, fallback: string) =>
  String(data?.name || data?.displayName || fallback).trim() || fallback;

const linkedId = (entry: any): string => {
  if (typeof entry === "string" || typeof entry === "number") return String(entry);
  if (!entry || typeof entry !== "object") return "";
  return String(entry.itemId || entry.id || "");
};

const cleanValue = (value: any): any => {
  if (value === undefined) return undefined;
  if (value === null || typeof value !== "object") return value;
  if (typeof value.toDate === "function" || typeof value._methodName === "string") return value;
  if (Array.isArray(value)) {
    return value.map(cleanValue).filter((entry) => entry !== undefined);
  }
  const next: Record<string, any> = {};
  Object.entries(value).forEach(([key, entry]) => {
    if (entry === undefined) return;
    const cleaned = cleanValue(entry);
    if (cleaned !== undefined) next[key] = cleaned;
  });
  return next;
};

const stamp = (data: Record<string, any>) => {
  const keys = ["created", "updated", "createdAt", "updatedAt"].filter((key) => key in data);
  const targets = keys.length > 0 ? keys : ["created", "updated"];
  targets.forEach((key) => {
    data[key] = serverTimestamp();
  });
};

const payloadFrom = (source: Record<string, any>, storeId: string) => {
  const data = cleanValue(cloneDeep(source)) as Record<string, any>;
  delete data.id;
  data.storeId = storeId;
  stamp(data);
  return data;
};

const mappedId = (ids: Map<string, string>, value: string) => {
  const next = ids.get(value);
  if (!next) throw new Error("A copied record is missing its new ID");
  return next;
};

const rewriteChecked = (
  list: any[],
  itemIds: Map<string, string>,
  categoryIds: Map<string, string>,
  menuIds: Map<string, string>,
  destinationStoreId: string
) => {
  const next: any[] = [];
  (Array.isArray(list) ? list : []).forEach((entry) => {
    const current = linkedId(entry);
    const mapped = itemIds.get(current);
    if (!mapped) return;
    if (typeof entry === "string" || typeof entry === "number") {
      next.push(mapped);
      return;
    }
    const copy = cloneDeep(entry);
    if (itemIds.has(String(copy.id || ""))) copy.id = mapped;
    if (itemIds.has(String(copy.itemId || ""))) copy.itemId = mapped;
    if (copy.storeId) copy.storeId = destinationStoreId;
    if (copy.menuId) {
      const nextMenu = menuIds.get(String(copy.menuId));
      if (nextMenu) copy.menuId = nextMenu;
      else delete copy.menuId;
    }
    if (copy.categoryId) {
      const nextCategory = categoryIds.get(String(copy.categoryId));
      if (nextCategory) copy.categoryId = nextCategory;
      else delete copy.categoryId;
    }
    delete copy.printerApiKeys;
    next.push(copy);
  });
  return next;
};

async function docsForStore(collectionName: string, storeId: string) {
  const snap = await getDocs(query(collection(db, collectionName), where("storeId", "==", storeId)));
  return snap.docs.map((entry) => ({ id: entry.id, data: entry.data() as Record<string, any> }));
}

async function loadBundle(sourceStoreId: string, menuId: string): Promise<MenuBundle> {
  const menuSnap = await getDoc(doc(db, "menus", menuId));
  if (!menuSnap.exists()) throw new Error("Menu was not found");
  const menuData = menuSnap.data() as Record<string, any>;
  if (String(menuData.storeId || "") !== sourceStoreId) throw new Error("Menu does not belong to the source store");

  const [storeCategories, menuCategories, storeItems, menuItems, storeTags, storeOptions, storeTopMenus, comboSnap, limitSnap] = await Promise.all([
    docsForStore("categories", sourceStoreId),
    getDocs(query(collection(db, "categories"), where("menuId", "==", menuId))).then((snap) =>
      snap.docs.map((entry) => ({ id: entry.id, data: entry.data() as Record<string, any> }))
    ),
    docsForStore("items", sourceStoreId),
    getDocs(query(collection(db, "items"), where("menuId", "==", menuId))).then((snap) =>
      snap.docs.map((entry) => ({ id: entry.id, data: entry.data() as Record<string, any> }))
    ),
    docsForStore("itemTags", sourceStoreId),
    docsForStore("advancedOption", sourceStoreId),
    docsForStore("topMenus", sourceStoreId),
    getDocs(collection(db, "stores", sourceStoreId, "combos")),
    docsForStore("catLimit", sourceStoreId),
  ]);

  const categories = new Map<string, SourceDoc>();
  [...storeCategories, ...menuCategories].forEach((entry) => {
    if (String(entry.data.menuId || "") === menuId) categories.set(entry.id, entry);
  });

  const items = new Map<string, SourceDoc>();
  const categoryIds = new Set(categories.keys());
  [...storeItems, ...menuItems].forEach((entry) => {
    const itemMenuId = String(entry.data.menuId || "");
    const itemCategoryId = String(entry.data.categoryId || "");
    if (itemMenuId === menuId || (!itemMenuId && categoryIds.has(itemCategoryId))) {
      items.set(entry.id, entry);
    }
  });

  const itemIds = new Set(items.keys());
  const tagIds = new Set<string>();
  items.forEach((item) => {
    (Array.isArray(item.data.tags) ? item.data.tags : []).forEach((tag) => {
      const id = typeof tag === "string" ? tag : String(tag?.id || "");
      if (id) tagIds.add(id);
    });
  });
  storeTags.forEach((tag) => {
    const checked = tag.data.items?.checkedItems || tag.data.checkedItems || [];
    if ((Array.isArray(checked) ? checked : []).some((entry) => itemIds.has(linkedId(entry)))) {
      tagIds.add(tag.id);
    }
  });

  const tags = storeTags.filter((tag) => tagIds.has(tag.id));
  const options = storeOptions.filter((option) => {
    const checked = option.data.items?.checkedItems || [];
    return (Array.isArray(checked) ? checked : []).some((entry) => itemIds.has(linkedId(entry)));
  });
  const topMenus = storeTopMenus.filter((menu) => {
    const list = Array.isArray(menu.data.categories) ? menu.data.categories : [];
    return list.some((entry) => categoryIds.has(linkedId(entry)));
  });
  const combos = comboSnap.docs
    .filter((entry) => itemIds.has(entry.id))
    .map((entry) => ({ id: entry.id, data: entry.data() as Record<string, any> }));
  const limits = limitSnap.filter((entry) => categoryIds.has(entry.id));

  const missingLimits = [...categoryIds].filter((id) => !limits.some((entry) => entry.id === id));
  if (missingLimits.length > 0) {
    const extra = await Promise.all(missingLimits.map(async (id) => {
      const snap = await getDoc(doc(db, "catLimit", id));
      if (!snap.exists()) return null;
      return { id: snap.id, data: snap.data() as Record<string, any> };
    }));
    extra.forEach((entry) => {
      if (entry) limits.push(entry);
    });
  }

  return {
    menu: { id: menuSnap.id, data: menuData },
    categories: [...categories.values()],
    items: [...items.values()],
    tags,
    options,
    topMenus,
    combos,
    limits,
  };
}

const categoryName = (categories: SourceDoc[], categoryId: string) => {
  const match = categories.find((entry) => entry.id === categoryId);
  return match ? nameOf(match.data, "Category") : "No category";
};

export async function loadMenuCopyPreview(sourceStoreId: string, menuId: string): Promise<MenuCopyPreview> {
  const bundle = await loadBundle(sourceStoreId, menuId);
  return {
    menu: { id: bundle.menu.id, name: nameOf(bundle.menu.data, "Menu") },
    categories: bundle.categories.map((entry) => ({
      id: entry.id,
      name: nameOf(entry.data, "Category"),
      itemCount: bundle.items.filter((item) => String(item.data.categoryId || "") === entry.id).length,
    })),
    items: bundle.items.map((entry) => ({
      id: entry.id,
      name: nameOf(entry.data, "Item"),
      image: String(entry.data.image || "").trim() || undefined,
      categoryName: categoryName(bundle.categories, String(entry.data.categoryId || "")),
    })),
    tags: bundle.tags.map((entry) => ({ id: entry.id, name: nameOf(entry.data, "Tag") })),
    options: bundle.options.map((entry) => ({ id: entry.id, name: nameOf(entry.data, "Advanced option") })),
    topMenus: bundle.topMenus.map((entry) => ({ id: entry.id, name: nameOf(entry.data, "Top menu") })),
    combos: bundle.combos.map((entry) => ({ id: entry.id, name: nameOf(entry.data, "Combo") })),
    categoryLimits: bundle.limits.map((entry) => ({ id: entry.id, name: nameOf(entry.data, "Category limit") })),
  };
}

export async function loadDestinationMenus(storeId: string): Promise<DestinationMenuPreview[]> {
  const [menus, categories] = await Promise.all([
    docsForStore("menus", storeId),
    docsForStore("categories", storeId),
  ]);
  return menus
    .map((menu) => ({
      id: menu.id,
      name: nameOf(menu.data, "Menu"),
      categoryCount: categories.filter((category) => String(category.data.menuId || "") === menu.id).length,
      order: Number(menu.data.order) || 0,
    }))
    .sort((left, right) => left.order - right.order)
    .map(({ order, ...menu }) => {
      void order;
      return menu;
    });
}

export async function listStoreMenus(storeId: string): Promise<NamedRecord[]> {
  const menus = await docsForStore("menus", storeId);
  return menus
    .map((menu) => ({ id: menu.id, name: nameOf(menu.data, "Menu"), order: Number(menu.data.order) || 0 }))
    .sort((left, right) => left.order - right.order)
    .map(({ order, ...menu }) => {
      void order;
      return menu;
    });
}

type PendingWrite = {
  ref: ReturnType<typeof doc>;
  data: Record<string, any>;
  countKey: keyof CopyCounts;
};

async function commitWrites(
  writes: PendingWrite[],
  counts: CopyCounts,
  label: string,
  onProgress?: (progress: CopyProgress) => void
) {
  let batch: WriteBatch = writeBatch(db);
  let pending = 0;
  for (let index = 0; index < writes.length; index += 1) {
    const write = writes[index];
    batch.set(write.ref, write.data);
    pending += 1;
    const last = index === writes.length - 1;
    if (pending === BATCH_LIMIT || last) {
      onProgress?.({ label, completed: index + 1, total: writes.length });
      try {
        await batch.commit();
      } catch (error) {
        const message = error instanceof Error ? error.message : "Copy failed";
        throw new MenuCopyError(message, { ...counts });
      }
      writes.slice(index + 1 - pending, index + 1).forEach((entry) => {
        counts[entry.countKey] += 1;
        if (entry.countKey === "items" && String(entry.data.image || "").trim()) counts.pictures += 1;
      });
      batch = writeBatch(db);
      pending = 0;
    }
  }
}

export async function copyMenu(input: {
  sourceStoreId: string;
  menuId: string;
  destinationStoreId: string;
  onProgress?: (progress: CopyProgress) => void;
}): Promise<CopyCounts> {
  const { sourceStoreId, menuId, destinationStoreId, onProgress } = input;
  if (!sourceStoreId || !menuId || !destinationStoreId) throw new Error("Choose a source menu and a destination store");
  if (sourceStoreId === destinationStoreId) throw new Error("Choose a different destination store");

  const counts = emptyCounts();
  const bundle = await loadBundle(sourceStoreId, menuId);
  const destinationMenus = await docsForStore("menus", destinationStoreId);
  const nextOrder = destinationMenus.reduce((highest, menu) => Math.max(highest, Number(menu.data.order) || 0), -1) + 1;

  const menuIdMap = new Map<string, string>([[bundle.menu.id, doc(collection(db, "menus")).id]]);
  const categoryIds = new Map(bundle.categories.map((entry) => [entry.id, doc(collection(db, "categories")).id]));
  const itemIds = new Map(bundle.items.map((entry) => [entry.id, doc(collection(db, "items")).id]));
  const tagIds = new Map(bundle.tags.map((entry) => [entry.id, doc(collection(db, "itemTags")).id]));
  const optionIds = new Map(bundle.options.map((entry) => [entry.id, doc(collection(db, "advancedOption")).id]));
  const topMenuIds = new Map(bundle.topMenus.map((entry) => [entry.id, doc(collection(db, "topMenus")).id]));

  try {
  const newMenuId = mappedId(menuIdMap, bundle.menu.id);
  const menuData = payloadFrom(bundle.menu.data, destinationStoreId);
  menuData.order = nextOrder;

  const categoryWrites: PendingWrite[] = bundle.categories.map((entry) => {
    const data = payloadFrom(entry.data, destinationStoreId);
    data.menuId = newMenuId;
    return { ref: doc(db, "categories", mappedId(categoryIds, entry.id)), data, countKey: "categories" };
  });

  const tagWrites: PendingWrite[] = bundle.tags.map((entry) => {
    const data = payloadFrom(entry.data, destinationStoreId);
    const checked = rewriteChecked(data.items?.checkedItems || data.checkedItems || [], itemIds, categoryIds, menuIdMap, destinationStoreId);
    if (data.items && typeof data.items === "object") data.items = { ...data.items, checkedItems: checked };
    else data.items = { checkedItems: checked };
    return { ref: doc(db, "itemTags", mappedId(tagIds, entry.id)), data, countKey: "tags" };
  });

  const itemWrites: PendingWrite[] = bundle.items.map((entry) => {
    const data = payloadFrom(entry.data, destinationStoreId);
    data.menuId = newMenuId;
    const nextCategory = categoryIds.get(String(entry.data.categoryId || ""));
    if (nextCategory) data.categoryId = nextCategory;
    else delete data.categoryId;
    delete data.printerApiKeys;
    if (Array.isArray(data.tags)) {
      data.tags = data.tags.flatMap((tag: any) => {
        if (typeof tag === "string") {
          const mapped = tagIds.get(tag);
          return mapped ? [mapped] : [tag];
        }
        const mapped = tagIds.get(String(tag?.id || ""));
        if (!tag?.id) return [tag];
        if (!mapped) return [];
        return [{ ...tag, id: mapped }];
      });
    }
    return { ref: doc(db, "items", mappedId(itemIds, entry.id)), data, countKey: "items" };
  });

  const optionWrites: PendingWrite[] = bundle.options.flatMap((entry) => {
    const data = payloadFrom(entry.data, destinationStoreId);
    const checked = rewriteChecked(data.items?.checkedItems || [], itemIds, categoryIds, menuIdMap, destinationStoreId);
    if (checked.length === 0) return [];
    data.items = { ...(data.items || {}), checkedItems: checked };
    return [{ ref: doc(db, "advancedOption", mappedId(optionIds, entry.id)), data, countKey: "options" as const }];
  });

  const topMenuWrites: PendingWrite[] = bundle.topMenus.flatMap((entry) => {
    const data = payloadFrom(entry.data, destinationStoreId);
    const categories = (Array.isArray(data.categories) ? data.categories : []).flatMap((category: any) => {
      const mapped = categoryIds.get(linkedId(category));
      if (!mapped) return [];
      if (typeof category === "string" || typeof category === "number") return [mapped];
      const copy = cloneDeep(category);
      copy.id = mapped;
      if (copy.menuId) copy.menuId = newMenuId;
      if (copy.storeId) copy.storeId = destinationStoreId;
      return [copy];
    });
    if (categories.length === 0) return [];
    data.categories = categories;
    return [{ ref: doc(db, "topMenus", mappedId(topMenuIds, entry.id)), data, countKey: "topMenus" as const }];
  });

  const comboWrites: PendingWrite[] = bundle.combos.map((entry) => {
    const data = payloadFrom(entry.data, destinationStoreId);
    const newItemId = mappedId(itemIds, entry.id);
    data.baseDishId = newItemId;
    if (Array.isArray(data.steps)) {
      data.steps = data.steps.map((step: any) => ({
        ...step,
        items: {
          ...(step?.items || {}),
          checkedItems: rewriteChecked(step?.items?.checkedItems || [], itemIds, categoryIds, menuIdMap, destinationStoreId),
        },
      }));
    }
    return {
      ref: doc(db, "stores", destinationStoreId, "combos", newItemId),
      data,
      countKey: "combos" as const,
    };
  });

  const limitWrites: PendingWrite[] = bundle.limits.map((entry) => {
    const data = payloadFrom(entry.data, destinationStoreId);
    return { ref: doc(db, "catLimit", mappedId(categoryIds, entry.id)), data, countKey: "categoryLimits" as const };
  });

  const menuWrite: PendingWrite = {
    ref: doc(db, "menus", newMenuId),
    data: menuData,
    countKey: "menu",
  };

    await commitWrites([menuWrite], counts, "Menu", onProgress);
    await commitWrites(categoryWrites, counts, "Categories", onProgress);
    await commitWrites(tagWrites, counts, "Tags", onProgress);
    await commitWrites(itemWrites, counts, "Items", onProgress);
    await commitWrites(optionWrites, counts, "Advanced options", onProgress);
    await commitWrites(topMenuWrites, counts, "Top menus", onProgress);
    await commitWrites(comboWrites, counts, "Combos", onProgress);
    await commitWrites(limitWrites, counts, "Category limits", onProgress);
  } catch (error) {
    if (error instanceof MenuCopyError) throw error;
    const message = error instanceof Error ? error.message : "Copy failed";
    throw new MenuCopyError(message, { ...counts });
  }

  return counts;
}
